import React, { useState, useMemo, useEffect } from 'react';
import { Layout } from 'tea-component';
import NavHader from '@/pages/home/components/NavHeader';
import { useLocation, useSelector } from 'umi';
import Mask from '@/pages/home/components/Mask';
import GuideModule from '@/pages/home/components/GuideModule';
import Auth from './Auth';
import { useReport } from '@/pages/home/log/reportPageLoad/reportPageLoad';
import './index.scss';
import {
  SYSTEM_THEME_KEY,
  SYSTEM_THEME_LIGHT,
  SYSTEM_THEME_DARK,
  SYSTEM_THEME_DARK_CLASS,
} from '@home/_config/config';
import AgentRobot from '@/pages/home/components/AgentRobot';
import { useEmbeddedPageOptions } from '@home/hooks/useEmbeddedPageOptions';
const { Header } = Layout;

/** 将主题落到 document.body（与 NavHeader Theme 切换逻辑保持一致） */
const applyThemeToDocument = (theme: string): void => {
  if (theme === SYSTEM_THEME_DARK) {
    document.body.classList.add(SYSTEM_THEME_DARK_CLASS);
  } else {
    document.body.classList.remove(SYSTEM_THEME_DARK_CLASS);
  }
};
const hideHeaderList = [
  '/home',
  '/login',
  '/login/thirdPartyLogin',
  '/unSubscribe',
  '/intelligence/download',
  '/intelligence/downloadCommon',
  '/intelligence/pdfPreviewDownload',
  '/intelligence/pdfPreviewDownloadLikeShare',
  '/opinion/download',
  '/datalab/download',
  '/g-pop/DocPreviewer',
  '/datalab/embedded',
  '/g-pop/VersionHistory',
];

/** 隐藏顶栏：pathname 模糊匹配（pathname 包含即隐藏，适合带动态参数的路由） */
const hideHeaderPathIncludes = ['/authEmbed'];

const AGENT_VISIBLE_PATH_PREFIXES = [
  '/home',
  '/intelligence',
  '/opinion',
  '/opinion',
  '/g-pop',
  '/studio',
  '/benchmark',
  '/aigc2',
  '/aigc',
  '/aigallery',
  '/gamewiki',
  '/chatgpt',
  '/dashboard',
  '/datalab',
  '/search',
  '/strategic',
  '/marketing',
];

// 需要隐藏全局水印的页面path集合
const HIDE_MASK_PAGE_PATHS = [
  '/intelligence/pdfPreviewDownload',
  '/intelligence/pdfPreviewDownloadLikeShare',
];

export const ThemeContext = React.createContext({} as any); // Context
const ThemeProvider = ThemeContext.Provider; // 提供者

interface ICommonWrapperProps {
  children?: React.ReactNode;
}

const CommonWrapper = (props: ICommonWrapperProps) => {
  const location: any = useLocation();
  const { hideTopNav, hideAgent } = useEmbeddedPageOptions();
  useReport(location);
  // 初始化本地已变主题色值，没有则取默认值
  const theme = localStorage.getItem(SYSTEM_THEME_KEY) || SYSTEM_THEME_LIGHT;
  // 深浅主题色值
  const [themeValue, setThemeValue] = useState<string>(theme);

  // 判断 URL 中是否存在 ViewportWidth 参数
  const isScreenshotMode = useMemo(() => {
    const { query = {} } = location;
    return query?.viewport_width;
  }, [location.query]);

  // 直接操作 body 的 class，不增加额外的 div 节点
  useEffect(() => {
    if (isScreenshotMode) {
      document.body.classList.add('dbh-screenshot-mode');
    } else {
      document.body.classList.remove('dbh-screenshot-mode');
    }
  }, [isScreenshotMode]);

  // 仅 iframe 内同步：父窗口切主题写 localStorage 后，子页通过 storage 更新 ThemeContext + body class
  useEffect(() => {
    // 顶层页面由 NavHeader Theme 直接改主题，无需再听 storage
    if (window.parent === window) {
      return;
    }
    const handleStorageChange = (event: StorageEvent): void => {
      if (event.key !== SYSTEM_THEME_KEY || event.newValue == null) {
        return;
      }
      const nextTheme = event.newValue;
      applyThemeToDocument(nextTheme);
      setThemeValue(nextTheme);
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const showHeader = useMemo(() => {
    const { pathname } = location;
    if (hideHeaderList.includes(pathname)) return false;
    if (hideHeaderPathIncludes.some((segment) => pathname.includes(segment))) {
      return false;
    }
    return true;
  }, [location]);

  const { userInfo } = useSelector((state: any) => {
    const {
      home: { userInfo },
    } = state;
    return {
      userInfo,
    };
  });

  const permissions = useMemo(() => {
    if (userInfo?.permissions) {
      const perms = Object.keys(userInfo.permissions);
      return perms;
    }
    return [];
  }, [userInfo?.permissions]);

  // AI工具权限
  const isHaveAIPermission = useMemo(
    () => permissions.includes('aigc') || permissions.includes('aigc_pc'),
    [permissions],
  );

  const showAgent = useMemo(() => {
    const { pathname } = location;
    const lowerPathname = pathname.toLowerCase();
    return (
      AGENT_VISIBLE_PATH_PREFIXES.some((prefix) =>
        lowerPathname.startsWith(prefix),
      ) && isHaveAIPermission
    );
  }, [location, isHaveAIPermission]);

  // 是否展示全局水印
  const showMask = useMemo<boolean>(() => {
    const { pathname } = location || {};
    return !HIDE_MASK_PAGE_PATHS.includes(pathname);
  }, [location]);

  return (
    <>
      <ThemeProvider value={{ themeValue, setThemeValue }}>
        {showHeader && !hideTopNav && (
          <Header className="layout__header">
            <NavHader />
          </Header>
        )}
        {/* dbh-body-wrap: 统一入口 flex 行容器，左侧页面内容 + 右侧 Copilot 停靠槽。
            Copilot docked 时通过 Portal 渲染进 #dbh-copilot-dock-portal，所有页面均生效。 */}
        <div className="dbh-body-wrap">
          <Auth>{props.children}</Auth>
          <div id="dbh-copilot-dock-portal" />
        </div>
        <GuideModule />
        {showMask && <Mask />}
        {showAgent && !hideAgent && <AgentRobot />}
      </ThemeProvider>
    </>
  );
};

export default CommonWrapper;
